import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Divider,
  Collapse,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  NetworkCheck as NetworkCheckIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import axios from '../api/axios';

const NetworkInterfaceManager = ({ deviceId }) => {
  const [interfaces, setInterfaces] = useState([]);
  const [expandedInterface, setExpandedInterface] = useState(null);
  const [interfaceIPs, setInterfaceIPs] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [editingInterface, setEditingInterface] = useState(null);
  const [openIPDialog, setOpenIPDialog] = useState(false);
  const [editingIP, setEditingIP] = useState(null);
  const [currentInterfaceId, setCurrentInterfaceId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state for interface
  const [interfaceForm, setInterfaceForm] = useState({
    name: '',
    label: '',
    mac_address: '',
    is_active: true,
    interface_type: 'ethernet',
    position: 'auto',
    offset_x: 0,
    offset_y: 0
  });

  // Form state for IP
  const [ipForm, setIPForm] = useState({
    ip: '',
    netmask: '',
    gateway: '',
    dns_primary: '',
    dns_secondary: '',
    is_reserved: false,
    comment: ''
  });

  useEffect(() => {
    fetchInterfaces();
  }, [deviceId]);

  const fetchInterfaces = async () => {
    try {
      const response = await axios.get(`/devices/${deviceId}`);
      setInterfaces(response.data.interfaces || []);
    } catch (err) {
      console.error('Error fetching interfaces:', err);
      setError('Ошибка загрузки интерфейсов');
    }
  };

  const fetchInterfaceIPs = async (interfaceId) => {
    try {
      const response = await axios.get(`/devices/${deviceId}/interfaces/${interfaceId}/ips`);
      setInterfaceIPs(prev => ({
        ...prev,
        [interfaceId]: response.data
      }));
    } catch (err) {
      console.error('Error fetching IPs:', err);
    }
  };

  const handleExpandInterface = (interfaceId) => {
    if (expandedInterface === interfaceId) {
      setExpandedInterface(null);
    } else {
      setExpandedInterface(interfaceId);
      if (!interfaceIPs[interfaceId]) {
        fetchInterfaceIPs(interfaceId);
      }
    }
  };

  const handleOpenInterfaceDialog = (iface = null) => {
    if (iface) {
      setEditingInterface(iface);
      setInterfaceForm({
        name: iface.name || '',
        label: iface.label || '',
        mac_address: iface.mac_address || '',
        is_active: iface.is_active !== undefined ? iface.is_active : true,
        interface_type: iface.interface_type || 'ethernet',
        position: iface.position || 'auto',
        offset_x: iface.offset_x || 0,
        offset_y: iface.offset_y || 0
      });
    } else {
      setEditingInterface(null);
      setInterfaceForm({
        name: '',
        label: '',
        mac_address: '',
        is_active: true,
        interface_type: 'ethernet',
        position: 'auto',
        offset_x: 0,
        offset_y: 0
      });
    }
    setOpenDialog(true);
  };

  const handleCloseInterfaceDialog = () => {
    setOpenDialog(false);
    setEditingInterface(null);
  };

  const handleSaveInterface = async () => {
    try {
      if (editingInterface) {
        await axios.put(`/devices/${deviceId}/interfaces/${editingInterface.id}`, interfaceForm);
        setSuccess('Интерфейс обновлен');
      } else {
        await axios.post(`/devices/${deviceId}/interfaces`, interfaceForm);
        setSuccess('Интерфейс создан');
      }
      handleCloseInterfaceDialog();
      fetchInterfaces();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка сохранения интерфейса');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeleteInterface = async (interfaceId) => {
    if (!window.confirm('Удалить этот интерфейс?')) return;

    try {
      await axios.delete(`/devices/${deviceId}/interfaces/${interfaceId}`);
      setSuccess('Интерфейс удален');
      fetchInterfaces();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления интерфейса');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleOpenIPDialog = (interfaceId, ip = null) => {
    setCurrentInterfaceId(interfaceId);
    if (ip) {
      setEditingIP(ip);
      setIPForm({
        ip: ip.ip || '',
        netmask: ip.netmask || '',
        gateway: ip.gateway || '',
        dns_primary: ip.dns_primary || '',
        dns_secondary: ip.dns_secondary || '',
        is_reserved: ip.is_reserved || false,
        comment: ip.comment || ''
      });
    } else {
      setEditingIP(null);
      setIPForm({
        ip: '',
        netmask: '',
        gateway: '',
        dns_primary: '',
        dns_secondary: '',
        is_reserved: false,
        comment: ''
      });
    }
    setOpenIPDialog(true);
  };

  const handleCloseIPDialog = () => {
    setOpenIPDialog(false);
    setEditingIP(null);
    setCurrentInterfaceId(null);
  };

  const handleSaveIP = async () => {
    try {
      await axios.post(`/devices/${deviceId}/interfaces/${currentInterfaceId}/ips`, {
        ip: ipForm.ip,
        interface_id: currentInterfaceId,
        is_reserved: ipForm.is_reserved,
        comment: ipForm.comment,
        netmask: ipForm.netmask || null,
        gateway: ipForm.gateway || null,
        dns_primary: ipForm.dns_primary || null,
        dns_secondary: ipForm.dns_secondary || null
      });
      setSuccess('IP адрес добавлен');
      handleCloseIPDialog();
      fetchInterfaceIPs(currentInterfaceId);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка добавления IP адреса');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeleteIP = async (interfaceId, ipId) => {
    if (!window.confirm('Удалить этот IP адрес?')) return;

    try {
      await axios.delete(`/devices/${deviceId}/interfaces/${interfaceId}/ips/${ipId}`);
      setSuccess('IP адрес удален');
      fetchInterfaceIPs(interfaceId);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления IP адреса');
      setTimeout(() => setError(''), 5000);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 2, pt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            ({interfaces.length})
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenInterfaceDialog()}
          >
            Добавить интерфейс
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {interfaces.length === 0 ? (
          <Typography color="text.secondary">Нет интерфейсов</Typography>
        ) : (
          interfaces.map((iface) => (
            <Box key={iface.id} sx={{ mb: 2 }}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {iface.name}
                      </Typography>
                      {iface.label && (
                        <Chip label={iface.label} size="small" variant="outlined" />
                      )}
                      <Chip
                        label={iface.is_active ? 'Активен' : 'Неактивен'}
                        size="small"
                        color={iface.is_active ? 'success' : 'default'}
                      />
                      <Chip
                        label={iface.interface_type || 'ethernet'}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <Box>
                      <IconButton size="small" onClick={() => handleOpenInterfaceDialog(iface)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteInterface(iface.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleExpandInterface(iface.id)}>
                        {expandedInterface === iface.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>

                  {iface.mac_address && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      MAC: {iface.mac_address}
                    </Typography>
                  )}

                  <Collapse in={expandedInterface === iface.id}>
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">IP адреса</Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenIPDialog(iface.id)}
                        >
                          Добавить IP
                        </Button>
                      </Box>
                      {interfaceIPs[iface.id] && interfaceIPs[iface.id].length > 0 ? (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>IP адрес</TableCell>
                                <TableCell>Маска</TableCell>
                                <TableCell>Шлюз</TableCell>
                                <TableCell>DNS 1</TableCell>
                                <TableCell>DNS 2</TableCell>
                                <TableCell>Статус</TableCell>
                                <TableCell align="right">Действия</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {interfaceIPs[iface.id].map((ip) => (
                                <TableRow key={ip.id}>
                                  <TableCell>{ip.ip}</TableCell>
                                  <TableCell>{ip.netmask || '-'}</TableCell>
                                  <TableCell>{ip.gateway || '-'}</TableCell>
                                  <TableCell>{ip.dns_primary || '-'}</TableCell>
                                  <TableCell>{ip.dns_secondary || '-'}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={ip.is_reserved ? 'Резерв' : 'Назначен'}
                                      size="small"
                                      color={ip.is_reserved ? 'warning' : 'success'}
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteIP(iface.id, ip.id)}
                                      color="error"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Нет IP адресов
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            </Box>
          ))
        )}

        {/* Interface Dialog */}
        <Dialog open={openDialog} onClose={handleCloseInterfaceDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingInterface ? 'Редактировать интерфейс' : 'Создать интерфейс'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Имя интерфейса"
                value={interfaceForm.name}
                onChange={(e) => setInterfaceForm({ ...interfaceForm, name: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Метка"
                value={interfaceForm.label}
                onChange={(e) => setInterfaceForm({ ...interfaceForm, label: e.target.value })}
                fullWidth
              />
              <TextField
                label="MAC адрес"
                value={interfaceForm.mac_address}
                onChange={(e) => setInterfaceForm({ ...interfaceForm, mac_address: e.target.value })}
                placeholder="00:00:00:00:00:00"
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Тип интерфейса</InputLabel>
                <Select
                  value={interfaceForm.interface_type}
                  label="Тип интерфейса"
                  onChange={(e) => setInterfaceForm({ ...interfaceForm, interface_type: e.target.value })}
                >
                  <MenuItem value="ethernet">Ethernet</MenuItem>
                  <MenuItem value="wifi">WiFi</MenuItem>
                  <MenuItem value="virtual">Virtual</MenuItem>
                  <MenuItem value="fiber">Fiber</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Позиция на карте</InputLabel>
                <Select
                  value={interfaceForm.position}
                  label="Позиция на карте"
                  onChange={(e) => setInterfaceForm({ ...interfaceForm, position: e.target.value })}
                >
                  <MenuItem value="auto">Авто</MenuItem>
                  <MenuItem value="top">Верх</MenuItem>
                  <MenuItem value="bottom">Низ</MenuItem>
                  <MenuItem value="left">Лево</MenuItem>
                  <MenuItem value="right">Право</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={interfaceForm.is_active}
                    onChange={(e) => setInterfaceForm({ ...interfaceForm, is_active: e.target.checked })}
                  />
                }
                label="Активен"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseInterfaceDialog} startIcon={<CancelIcon />}>
              Отмена
            </Button>
            <Button onClick={handleSaveInterface} variant="contained" startIcon={<SaveIcon />}>
              Сохранить
            </Button>
          </DialogActions>
        </Dialog>

        {/* IP Address Dialog */}
        <Dialog open={openIPDialog} onClose={handleCloseIPDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Добавить IP адрес</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="IP адрес"
                value={ipForm.ip}
                onChange={(e) => setIPForm({ ...ipForm, ip: e.target.value })}
                placeholder="192.168.1.10"
                required
                fullWidth
              />
              <TextField
                label="Маска подсети"
                value={ipForm.netmask}
                onChange={(e) => setIPForm({ ...ipForm, netmask: e.target.value })}
                placeholder="255.255.255.0 или /24"
                fullWidth
              />
              <TextField
                label="Шлюз"
                value={ipForm.gateway}
                onChange={(e) => setIPForm({ ...ipForm, gateway: e.target.value })}
                placeholder="192.168.1.1"
                fullWidth
              />
              <TextField
                label="DNS Primary"
                value={ipForm.dns_primary}
                onChange={(e) => setIPForm({ ...ipForm, dns_primary: e.target.value })}
                placeholder="8.8.8.8"
                fullWidth
              />
              <TextField
                label="DNS Secondary"
                value={ipForm.dns_secondary}
                onChange={(e) => setIPForm({ ...ipForm, dns_secondary: e.target.value })}
                placeholder="8.8.4.4"
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={ipForm.is_reserved}
                    onChange={(e) => setIPForm({ ...ipForm, is_reserved: e.target.checked })}
                  />
                }
                label="Зарезервирован"
              />
              <TextField
                label="Комментарий"
                value={ipForm.comment}
                onChange={(e) => setIPForm({ ...ipForm, comment: e.target.value })}
                multiline
                rows={2}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseIPDialog} startIcon={<CancelIcon />}>
              Отмена
            </Button>
            <Button onClick={handleSaveIP} variant="contained" startIcon={<SaveIcon />}>
              Добавить
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
};

export default NetworkInterfaceManager;
